import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { PolicySettingModel } from '../../../../models';
import { Save, Loader2, ShieldCheck } from 'lucide-react';

const PolicySetting: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [formData, setFormData] = useState<PolicySettingModel>({
        tos: '',
        privacy: '',
        refund: ''
    });

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                // Fetch Policy Settings
                const docRef = doc(db, 'settings', 'policy');
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    setFormData(docSnap.data() as PolicySettingModel);
                }
            } catch (error) {
                console.error("Error fetching policy settings:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        try {
            await setDoc(doc(db, 'settings', 'policy'), formData);
            alert('약관 정보가 저장되었습니다.');
        } catch (error) {
            console.error("Error saving policy settings:", error);
            alert('저장 중 오류가 발생했습니다.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center p-20">
            <Loader2 className="animate-spin text-indigo-600" size={32} />
        </div>
    );

    return (
        <form onSubmit={handleSave} className="space-y-6">
            <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm flex items-center justify-between">
                <div className="space-y-1">
                    <h3 className="text-xl font-black text-slate-800 flex items-center gap-2">
                        <ShieldCheck className="text-indigo-600" size={24} />
                        약관 및 방침 설정
                    </h3>
                    <p className="text-slate-400 text-xs font-medium">이용약관 및 개인정보처리방침 내용을 관리합니다.</p>
                </div>
                <button
                    type="submit"
                    disabled={saving}
                    className="flex items-center gap-2 bg-indigo-600 text-white px-8 py-3 rounded-xl text-xs font-black shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all disabled:bg-slate-300"
                >
                    {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    정책 일괄 저장
                </button>
            </div>

            <div className="space-y-6">
                <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-4">
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-black text-slate-800 uppercase tracking-widest pl-1">서비스 이용약관</label>
                    </div>
                    <textarea
                        name="tos"
                        value={formData.tos}
                        onChange={handleChange}
                        placeholder="이용약관 내용을 입력하세요..."
                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500 min-h-[300px] resize-y transition-all font-mono leading-relaxed"
                    />
                </div>

                <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-4">
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-black text-slate-800 uppercase tracking-widest pl-1">개인정보처리방침</label>
                    </div>
                    <textarea
                        name="privacy"
                        value={formData.privacy}
                        onChange={handleChange}
                        placeholder="개인정보처리방침 내용을 입력하세요..."
                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500 min-h-[300px] resize-y transition-all font-mono leading-relaxed"
                    />
                </div>

                <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm space-y-4">
                    <div className="flex items-center justify-between">
                        <label className="text-xs font-black text-slate-800 uppercase tracking-widest pl-1">취소 및 환불 정책</label>
                    </div>
                    <textarea
                        name="refund"
                        value={formData.refund}
                        onChange={handleChange}
                        placeholder="환불 정책 내용을 입력하세요..."
                        className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-5 py-4 text-sm font-medium outline-none focus:ring-2 focus:ring-indigo-500 min-h-[200px] resize-y transition-all font-mono leading-relaxed"
                    />
                </div>
            </div>
        </form>
    );
};

export default PolicySetting;
